import { useEffect, useState } from "react";
import { FaArrowRight, FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { errorMessage } from "../../utils/utils";
import Spinner from "../../components/spinner";
import api from "../../utils/api";
import AuthShell, { authButton, authInput, authLabel, authLink } from "../../components/AuthShell";

export default function SignIn() {

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [remember, setRemember] = useState(false);

  useEffect(() => {
    const _temp = window.localStorage.temp;
    if (_temp) {
      setEmail(_temp);
      setRemember(true);
    }

    if (window.localStorage.token) {
      window.location.replace('/dashboard');
    }

  }, []);

  async function signIn(e: any) {
    e.preventDefault();

    window.localStorage.removeItem('refresh');
    window.localStorage.removeItem('token');
    window.localStorage.removeItem('id');
    window.localStorage.removeItem('email');
    window.localStorage.removeItem('name');
    setShowPass(false);
    setError('');
    setLoading(true);

    try {
      const res = await api.post(`/auth/signin`, { email, password });
      const json = await res.json();
      if (res.ok) {
        if (remember) {
          window.localStorage.temp = email;
        }
        window.localStorage.token = json.token;
        window.localStorage.refresh = json.refresh;
        window.localStorage.id = json.id;
        window.localStorage.email = json.email;
        window.localStorage.name = json.name;
        window.location.replace('/dashboard');
        return;

      } else {
        setError(errorMessage(json));
      }
    } catch (err) {
      setError(errorMessage(err));
    }
    setLoading(false);
  }

  return (
    <AuthShell
      title="Sign in to your account"
      subtitle="Enter your details to reach your remote accounts."
      banner={
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">

          <div className="flex flex-row items-center gap-3">
            <img src="/mikrouter-logo.svg" alt="" className="h-11 w-11 shrink-0 rounded-xl shadow-sm" />
            <div className="min-w-0">
              <div className="flex flex-row items-center gap-2">
                <span className="text-[15px] font-bold tracking-tight text-slate-900">MikRouter</span>
                <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 ring-1 ring-inset ring-emerald-200">
                  New
                </span>
              </div>
              <div className="text-xs text-slate-500">mikrouter.com</div>
            </div>
          </div>

          <p className="mt-3 text-[13px] leading-relaxed text-slate-600">
            Our new website is <span className="font-bold text-emerald-700">more affordable</span> and comes with faster
            servers, a redesigned dashboard and improved remote management.
          </p>

          <a
            href="https://mikrouter.com"
            target="_blank"
            rel="noreferrer"
            className="group mt-4 flex min-h-[42px] w-full flex-row items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-slate-950">
            Visit mikrouter.com
            <FaArrowRight className="transition-transform group-hover:translate-x-1" />
          </a>

        </div>
      }
      footer={<>
        <div className="text-slate-500">
          Don't have an account? <a href="/signup" className={authLink}>Sign Up</a>
        </div>
        <a href="https://kptd.ph/remote" className="font-semibold text-slate-500 transition hover:text-slate-900">
          Go back to home
        </a>
      </>}>

      <form onSubmit={signIn}>

        <div className="mb-4">
          <label htmlFor="email" className={authLabel}>Email address</label>
          <div className="mt-1.5">
            <input
              id="email"
              name="email"
              type="email"
              required
              value={email}
              disabled={loading}
              autoComplete="email"
              placeholder="name@domain.com"
              className={authInput}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="password" className={authLabel}>Password</label>
          <div className="relative mt-1.5">
            <input
              id="password"
              name="password"
              type={showPass ? 'text' : "password"}
              required
              value={password}
              disabled={loading}
              placeholder="Enter password"
              autoComplete="current-password"
              className={`${authInput} pr-11`}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              aria-label={showPass ? 'Hide password' : 'Show password'}
              className="absolute inset-y-0 end-0 flex items-center px-3 text-slate-400 transition hover:text-slate-600"
              onClick={loading ? undefined : () => setShowPass(!showPass)}>
              {showPass ? <FaRegEye className="text-emerald-600" /> : <FaRegEyeSlash />}
            </button>
          </div>
        </div>

        <div className="mb-5 flex items-center justify-between">
          <label htmlFor="remember-me" className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-600">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              checked={remember}
              disabled={loading}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              onChange={(e) => setRemember(e.target.checked)}
            />
            Remember me
          </label>

          <a href="/password-reset" className="text-sm font-semibold text-slate-500 transition hover:text-emerald-600">
            Forgot password?
          </a>
        </div>

        {error && (
          <div className="mb-5 rounded-xl bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-700 ring-1 ring-red-100">
            <div dangerouslySetInnerHTML={{ __html: error }} />
          </div>
        )}

        <button type="submit" disabled={loading} className={authButton}>
          {loading && <Spinner className="w-4 h-4" />} Sign in
        </button>

      </form>

    </AuthShell>
  )
}
